import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Lock, Eye, EyeOff, Trash2, ShieldCheck, KeyRound, Database, Mail } from "lucide-react";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 380, height: 380, background: "#15FF73", top: -120, right: -80 }} />
      <NavBar />
      <div className="max-w-3xl mx-auto px-5 lg:px-10 py-14 relative">
        <span className="sticker sticker-magenta mb-3 inline-block"><Lock size={11} strokeWidth={2.4} /> Privacy</span>
        <h1 className="font-display text-6xl sm:text-7xl font-extrabold leading-[0.88] tracking-[-0.04em] mt-3 mb-5">
          What we keep. <span className="text-rainbow">What we don't.</span>
        </h1>
        <p className="text-lg text-[#0B0F0C]/70 mb-12 max-w-2xl leading-relaxed">
          We built X-Seed to be a tool, not a tracker. The default is anonymity. Identity stays in the shadows. The work speaks. <span className="font-script text-2xl text-[#FF1A8C]">— XN</span>
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-12">
          <Card icon={<EyeOff size={18} />} tone="card-green" title="What we DON'T do">
            <ul className="space-y-2 text-sm">
              <li>· Sell, share, or rent your data — ever</li>
              <li>· Run third-party fingerprinting trackers</li>
              <li>· Force a real name. Use a handle.</li>
              <li>· Keep your real-world location — only state-level</li>
              <li>· Read your private session-room messages (encrypted)</li>
            </ul>
          </Card>
          <Card icon={<Eye size={18} />} tone="card-cyan" title="What we DO keep">
            <ul className="space-y-2 text-sm">
              <li>· Email + name — <b>encrypted at rest</b> (AES via Fernet)</li>
              <li>· Public handle — that's your face here</li>
              <li>· XP, streaks, posts, claims — tied to your handle</li>
              <li>· State you operate in — for legal compliance only</li>
              <li>· One Stripe customer ID per top-up — no card numbers</li>
            </ul>
          </Card>
        </div>

        <Card icon={<KeyRound size={18} />} tone="card-magenta" title="Encryption">
          <p className="text-sm leading-relaxed">
            Sensitive PII fields (<code className="bg-[#FFE74C]/40 px-1 rounded">email</code>, <code className="bg-[#FFE74C]/40 px-1 rounded">name</code>) are encrypted at rest using <b>Fernet (AES-128-CBC + HMAC-SHA256)</b>. Lookups use a deterministic HMAC index so the database never sees plaintext. If our DB ever leaked, all an attacker would see is encrypted ciphertext and your public handle. Session-room messages use ephemeral per-room keys and self-destruct on TTL.
          </p>
        </Card>

        <Card icon={<Database size={18} />} tone="card-sunset" title="Where it lives">
          <p className="text-sm leading-relaxed">
            One MongoDB cluster. No third-party analytics. No Google Analytics. No Facebook Pixel. No HotJar. No nothing. Brevo only sees the encrypted email when we fire a transactional email and they delete it on rotation. Stripe handles payment data on their side — we never touch a card number.
          </p>
        </Card>

        <Card icon={<Trash2 size={18} />} tone="card-purple" title="Delete everything">
          <p className="text-sm leading-relaxed mb-3">
            One tap from your profile menu nukes your account. We <b>soft-delete</b> immediately (PII scrubbed from queries) and <b>hard-delete</b> all rows after 30 days — including posts, comments, claims, cart, push subscriptions, session history. Can't be reversed after 30 days. That's the point.
          </p>
          <Link to="/me" className="btn-x btn-primary !py-2 !px-4 !text-xs">Manage your account →</Link>
        </Card>

        <Card icon={<ShieldCheck size={18} />} tone="card-green" title="21+ · Compliance">
          <p className="text-sm leading-relaxed">
            Cannabis is regulated. We only operate where it's legal. We refuse facilitation of unlicensed sales in any form — every transaction routes through a licensed dispensary partner. Session rooms are for connoisseur conversation + tipping the host's wallet. Selling is a hard no. We comply with state-by-state law.
          </p>
        </Card>

        <Card icon={<Mail size={18} />} tone="card-cyan" title="Questions / takedowns">
          <p className="text-sm leading-relaxed">
            <a href="mailto:founders@xseed.guru" className="text-[#FF1A8C] font-bold underline">founders@xseed.guru</a> · we read every message.
          </p>
        </Card>

        <div className="mt-12 text-[10px] font-mono-x font-bold uppercase tracking-widest opacity-50 text-center">
          Last updated · {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Card({ icon, title, tone, children }) {
  return (
    <div className={`card-x ${tone} bg-[#FAF6E8] p-6 mb-4`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h2 className="font-display text-2xl font-extrabold">{title}</h2>
      </div>
      <div className="text-[#0B0F0C]/85">{children}</div>
    </div>
  );
}
